import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

export const RED = '#e74c3c';

const badgeStyle = (color) => ({ background: color, color: '#fff', fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 10, minWidth: 20, textAlign: 'center', marginLeft: 6, lineHeight: '16px' });

export const Badge = ({ count, color, testId, onClick, title }) => (count > 0 ? (
  <span data-testid={testId} title={title} style={{ ...badgeStyle(color), cursor: onClick ? 'pointer' : 'default' }} onClick={onClick ? (e) => { e.preventDefault(); e.stopPropagation(); onClick(); } : undefined}>{count > 99 ? '99+' : count}</span>
) : null);

// One top-level menu (click to open; ArrowDown/Enter/Space open, Arrow keys move, Escape closes, Tab/outside click closes).
export const NavMenu = ({ group, active, badgeTotal }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const focusItem = (dir) => {
    const items = Array.from(ref.current?.querySelectorAll('[role="menuitem"]') || []);
    if (!items.length) return;
    const i = items.indexOf(document.activeElement);
    const next = dir === 'first' ? 0 : dir === 'last' ? items.length - 1 : (i + (dir === 'down' ? 1 : -1) + items.length) % items.length;
    items[next].focus();
  };
  const onTriggerKey = (e) => {
    if (['ArrowDown', 'Enter', ' '].includes(e.key)) { e.preventDefault(); setOpen(true); setTimeout(() => focusItem('first'), 0); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setOpen(true); setTimeout(() => focusItem('last'), 0); }
    if (e.key === 'Escape') setOpen(false);
  };
  const onMenuKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); focusItem('down'); }
    if (e.key === 'ArrowUp') { e.preventDefault(); focusItem('up'); }
    if (e.key === 'Home') { e.preventDefault(); focusItem('first'); }
    if (e.key === 'End') { e.preventDefault(); focusItem('last'); }
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); ref.current?.querySelector('button')?.focus(); }
    if (e.key === 'Tab') setOpen(false);
  };
  const Icon = group.icon;
  return (
    <div ref={ref} style={{ position: 'relative' }} onKeyDown={onMenuKey}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} data-testid={`nav-group-${group.id}`} onClick={() => setOpen((v) => !v)} onKeyDown={onTriggerKey}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 14, color: '#fff', whiteSpace: 'nowrap',
          background: active ? RED : (open ? 'rgba(255,255,255,0.10)' : 'transparent'), transition: 'background-color .15s' }}>
        {Icon && <Icon size={16} className="dw-nav-group-icon" />} <span className="dw-nav-label-long">{group.label}</span><span className="dw-nav-label-short">{group.short || group.label}</span>
        {badgeTotal > 0 && !open && <span style={{ width: 7, height: 7, borderRadius: 99, background: '#f59e0b', marginLeft: 2 }} data-testid={`nav-group-${group.id}-dot`} />}
        <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s', opacity: 0.8 }} />
      </button>
      {open && (
        <div role="menu" aria-label={group.label} data-testid={`nav-menu-${group.id}`}
          style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, minWidth: 230, background: '#16213e', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: 6, boxShadow: '0 16px 40px rgba(0,0,0,0.5)', zIndex: 60 }}>
          {group.items.map((it) => <MenuItem key={it.to + it.label} item={it} />)}
        </div>
      )}
    </div>
  );
};

export const MenuItem = ({ item, mobile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = item.isActive ? item.isActive(location) : (location.pathname === item.to.split('?')[0] || (item.to !== '/' && location.pathname.startsWith(item.to.split('?')[0])));
  const Icon = item.icon;
  return (
    <Link to={item.to} role="menuitem" data-testid={item.testId} aria-current={isActive ? 'page' : undefined}
      onFocus={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.10)'; }} onBlur={(e) => { e.currentTarget.style.background = isActive ? 'rgba(231,76,60,0.22)' : 'transparent'; }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.10)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = isActive ? 'rgba(231,76,60,0.22)' : 'transparent'; }}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: mobile ? '12px 14px' : '9px 12px', borderRadius: 7, color: '#fff', textDecoration: 'none', fontSize: 14, outline: 'none',
        background: isActive ? 'rgba(231,76,60,0.22)' : 'transparent', borderLeft: isActive ? `3px solid ${RED}` : '3px solid transparent' }}>
      {Icon && <Icon size={16} style={{ opacity: 0.85 }} />}
      <span style={{ flex: 1 }}>{item.label}</span>
      {item.badge && <Badge count={item.badge.count} color={item.badge.color} testId={item.badge.testId} title={item.badge.title} onClick={item.badge.to ? () => navigate(item.badge.to) : undefined} />}
    </Link>
  );
};
